import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FolderIcon from '@mui/icons-material/Folder'
import DescriptionIcon from '@mui/icons-material/Description'
import { ThorJavaMenuItemResponse } from '../../types/api'

interface ThorJavaMenuTreeViewProps {
  items: ThorJavaMenuItemResponse[]
  onItemClick?: (item: ThorJavaMenuItemResponse) => void
  defaultExpanded?: boolean
}

const ThorJavaMenuItemTree: React.FC<{
  item: ThorJavaMenuItemResponse
  depth?: number
  onItemClick?: (item: ThorJavaMenuItemResponse) => void
  defaultExpanded?: boolean
}> = ({ item, depth = 0, onItemClick, defaultExpanded = true }) => {
  const [open, setOpen] = useState(defaultExpanded && depth < 2)
  const hasChildren = item.children && item.children.length > 0
  const isFolder = item.infolder === 'S'
  const isSeparator = item.inseparador === 'S'

  // Se for separador, renderizar como linha divisória
  if (isSeparator) {
    return (
      <Box sx={{ pl: `${depth * 4}rem`, py: 1 }}>
        <Divider />
      </Box>
    )
  }

  // Calcular indentação: 4rem por nível para melhor visualização da hierarquia
  const indentSize = depth * 4

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          position: 'relative',
        }}
      >
        {/* Linha vertical de conexão hierárquica */}
        {depth > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: `${(depth - 1) * 4}rem`,
              top: 0,
              bottom: 0,
              width: '2px',
              borderLeft: '2px dashed',
              borderColor: 'divider',
              opacity: 0.5,
            }}
          />
        )}

        <Box sx={{ pl: `${indentSize}rem`, width: '100%' }}>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                setOpen(!open)
              } else if (onItemClick) {
                onItemClick(item)
              }
            }}
            sx={{
              py: 0.5,
              minHeight: 40,
              pl: 2,
              ml: depth > 0 ? '1.5rem' : 0,
              borderLeft: depth > 0 ? '3px solid' : 'none',
              borderColor: depth > 0 ? 'primary.light' : 'transparent',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: depth > 0 ? 'primary.main' : 'transparent',
                borderLeftWidth: depth > 0 ? '4px' : 0,
              },
            }}
          >
          {hasChildren && (
            <ListItemIcon sx={{ minWidth: 32 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  setOpen(!open)
                }}
                sx={{ p: 0.5 }}
              >
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            </ListItemIcon>
          )}
          {!hasChildren && <ListItemIcon sx={{ minWidth: 32 }} />}
          
          <ListItemIcon sx={{ minWidth: 32 }}>
            {isFolder ? (
              <FolderIcon fontSize="small" color="primary" />
            ) : (
              <DescriptionIcon fontSize="small" color="action" />
            )}
          </ListItemIcon>

          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title={item.nmtooltip || item.nmmenu} arrow>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: depth === 0 ? 600 : 400,
                      color: depth === 0 ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {item.nmmenu}
                  </Typography>
                </Tooltip>
                
                {item.nmmetodo && (
                  <Chip
                    label={item.nmmetodo}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                
                {isFolder && (
                  <Chip
                    label="Pasta"
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}

                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ ml: 'auto', fontSize: '0.65rem' }}
                >
                  N{item.nivel} S{item.cnseque}
                </Typography>
              </Box>
            }
            secondary={
              item.nmtooltip && item.nmtooltip !== item.nmmenu ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {item.nmtooltip}
                </Typography>
              ) : null
            }
            />
          </ListItemButton>
        </Box>
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children!.map((child) => (
              <ThorJavaMenuItemTree
                key={child.id}
                item={child}
                depth={depth + 1}
                onItemClick={onItemClick}
                defaultExpanded={defaultExpanded}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}

const ThorJavaMenuTreeView: React.FC<ThorJavaMenuTreeViewProps> = ({ 
  items, 
  onItemClick,
  defaultExpanded = true 
}) => {
  if (!items || items.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nenhum item de menu encontrado
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 2, maxHeight: '70vh', overflowY: 'auto' }}>
      <List>
        {items.map((item) => (
          <ThorJavaMenuItemTree
            key={item.id}
            item={item}
            onItemClick={onItemClick}
            defaultExpanded={defaultExpanded}
          />
        ))}
      </List>
    </Paper>
  )
}

export default ThorJavaMenuTreeView

